export type Sex = "F" | "M";
/** Which deck the user is browsing: girls, boys, or both ("X" = not sure yet). */
export type SexPref = "F" | "M" | "X";
/** Which decks a name belongs to: girls only, boys only, or both. */
export type GenderGroup = "F" | "M" | "B";
/** Index into per-sex tuples: 0 = girls, 1 = boys. */
export type SexIdx = 0 | 1;

/** Tag bit flags, mirrored from scripts/build_data.py. */
export const TAG = {
  NEW: 1,
  CLASSIC: 2,
  VINTAGE: 4,
  UNISEX: 8,
  RISING: 16,
  RISING_FAST: 32,
  FALLING: 64,
  SCOTLAND: 128,
  NI: 256,
  TIMELESS: 512,
  EDWARDIAN: 1024,
  /** a less established spelling of a name that sounds the same (Jaxon for Jackson) */
  ALT_SPELLING: 2048,
  /** began as a pet or short form (Alfie, Millie, Theo) */
  SHORT_FORM: 4096,
} as const;

/** "other": for families elsewhere, the UK and US records added together */
export type CountryId = "uk" | "us" | "other";

export interface NameEntry {
  idx: number;
  name: string;
  key: string;
  g: GenderGroup;
  /** share of recent babies with this name who are girls, 0-100 */
  fs: number;
  /** latest-year national rank [girls, boys]; 0 = not recorded that year */
  r: [number, number];
  /** latest-year national count [girls, boys] */
  c: [number, number];
  /** national rank five years before the latest year */
  r5: [number, number];
  /** % change in babies, last 3 years vs three years before that; null when too few to say */
  t: [number | null, number | null];
  /** sparkline per sex (base64url, one char per year) */
  s: [string, string];
  peakYear: number;
  /** most babies (dominant sex) in any single year */
  peakCount: number;
  peakRank: number;
  firstYear: number;
  tags: number;
  syl: number;
  origin: string | null;
  meaning: string;
  /** pronunciation respelling, e.g. EE-fa (AI-written) */
  pron: string;
  msrc: "W" | "A" | "";
  /** where there's no meaning, a short note on where the name comes from (AI-written) */
  originNote: string;
  len: number;
  initial: string;
}

export interface Region {
  id: string;
  name: string;
  years: [number, number];
}

export interface CountryMeta {
  id: CountryId;
  /** "the UK" */
  name: string;
  /** "UK" */
  short: string;
  /** first year of full national lists */
  since: number;
  /** names given to fewer babies a year aren't published */
  minListed: number;
  regions: Region[];
}

export interface SourceInfo {
  id: string;
  title: string;
  publisher: string;
  url: string;
  licence: string;
  years?: string;
  retrieved?: string;
}

export interface CoreData {
  v: string;
  built: string;
  country: CountryMeta;
  /** first year of the card window (sparklines, births) */
  y0: number;
  latest: number;
  births: { F: number[]; M: number[] };
  origins: string[];
  sources: SourceInfo[];
  entries: NameEntry[];
  byKey: Map<string, NameEntry>;
}

/** Series are [firstYear, v, v, ...] (zero-trimmed). */
export type Series = number[];

export interface SexDetail {
  /** babies per year, nationally */
  t?: Series;
  /** national rank per year */
  rk?: Series;
  /** a region's series, by region id (UK: ew, sc, ni; other: us, uk) */
  ew?: Series;
  sc?: Series;
  ni?: Series;
  us?: Series;
  uk?: Series;
  /** a region whose lists start earlier than the national ones (Scotland, from 1974) */
  early?: Series;
  /** historic top 100 by decade: [year, rank] */
  hist?: [number, number][];
  /** latest-year rank within each region */
  nr?: Record<string, number>;
  /** names closest in style, most alike first (scripts/similar.py) */
  like?: string[];
}

export interface Meaning {
  origin?: string;
  origin_detail?: string;
  meaning?: string;
  text?: string;
  source: "wiktionary" | "ai";
  url?: string;
  /** the name's Wiktionary entry, linked from an AI-written note */
  wkt?: string;
  nicknames?: string[];
  related?: string[];
  pron?: string;
  /** the names this note is borrowed from: an established spelling, or a double name's parts */
  of?: string[];
}

export interface NameDetailData {
  n: string;
  F?: SexDetail;
  M?: SexDetail;
  var?: string[];
  mean?: Meaning;
}

export interface TopData {
  years: number[];
  names: string[];
  /** per year, indexes into names in rank order */
  F: number[][];
  M: number[][];
  Fc: number[][];
  Mc: number[][];
}

/** [lead key or name, combined count, the lead's own rank, how many spellings] */
export type RealRow = [string, number, number, number];
/** [key, rank now, rank the year before] */
export type MoveRow = [string, number, number];
/** [name, rank here, rank there (0: not listed), babies here, babies there] */
export type AtlanticRow = [string, number, number, number, number];

export interface EndingSeries {
  /** "a", "o", "n"... or "ee" for an ee sound (Alfie, Lily, Teddy) */
  e: string;
  /** share of babies each year, in tenths of a per cent */
  s: number[];
  /** the most popular names with this ending in the latest year */
  ex: string[];
}

/** The stories in a country's records (scripts/insights.py), for Trends and the name pages. */
export interface InsightsData {
  y0: number;
  latest: number;
  /** spellings that sound the same, latest year: [combined count, combined rank, key, count, key, count...] */
  families: Record<Sex, (string | number)[][]>;
  /** the top 20 with every spelling counted */
  real: Record<Sex, RealRow[]>;
  /** the top 100's biggest moves since the year before */
  moves?: Record<Sex, { up: MoveRow[]; down: MoveRow[]; in: MoveRow[]; out: MoveRow[] }>;
  /** per year: how many names cover half of all babies, and the top 10's share (tenths of a per cent) */
  variety: Record<Sex, { half: number[]; top10: number[] }>;
  endings: Record<Sex, EndingSeries[]>;
  /** names whose balance of girls and boys has changed most: girls' share each year from `y`, in
   * tenths of a per cent; -1 = too few babies that year to say */
  cross: { k: string; y: number; s: number[] }[];
  /** names far more popular in one country than the other */
  atlantic: { years: { uk: number; us: number }; uk: Record<Sex, AtlanticRow[]>; us: Record<Sex, AtlanticRow[]> };
}

export interface RareData {
  n: string[];
  g: Sex[];
  t: number[];
  fy: number[];
  ly: number[];
}

/** 2 = love, 1 = like, -1 = nope */
export type Verdict = 2 | 1 | -1;

export interface Decision {
  d: Verdict;
  /** epoch ms when decided */
  t: number;
  /** display name, kept for rare names that aren't in the swipe deck */
  n?: string;
}
